import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { TranslationKey } from '@/types/i18n.types';
import {
    IPaymentDefaults,
    IPaymentOptions,
    IPaymentOptionsForm,
    toPaymentOptionsPayload,
} from '../models/payment-options.model';

const PAYMENT_OPTIONS_KEY = 'itinerary-payment-options';
const PAYMENT_DEFAULTS_KEY = 'payment-defaults';

/**
 * Metodi di pagamento di un itinerario, per il TA (con coordinate predefinite e stato
 * Stripe di chi incassa) e per il traveler autenticato (solo metodi e coordinate).
 * Il cliente anonimo dello shared link non passa di qui: li riceve in get-shared-itinerary.
 */
export const useItineraryPaymentOptions = (itineraryId?: string, enabled = true) => {
    const query = useQuery<IPaymentOptions>({
        queryKey: [PAYMENT_OPTIONS_KEY, itineraryId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_itinerary_payment_options' as any, {
                p_itinerary_id: itineraryId,
            });
            if (error) throw error;
            return data as IPaymentOptions;
        },
        enabled: !!itineraryId && enabled,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    return { options: query.data, isLoading: query.isLoading, error: query.error };
};

export const useSaveItineraryPaymentOptions = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ itineraryId, form }: { itineraryId: string; form: IPaymentOptionsForm }) => {
            const { data, error } = await supabase.rpc('set_itinerary_payment_options' as any, {
                p_itinerary_id: itineraryId,
                p_options: toPaymentOptionsPayload(form),
            });
            if (error) throw error;
            return data as IPaymentOptions;
        },
        onSuccess: (data, { itineraryId }) => {
            queryClient.setQueryData([PAYMENT_OPTIONS_KEY, itineraryId], data);
        },
    });
};

/** Coordinate bancarie predefinite di chi incassa, per la pagina Impostazioni. */
export const usePaymentDefaults = () => {
    const { userData } = useAuth();
    const queryClient = useQueryClient();

    const query = useQuery<IPaymentDefaults>({
        queryKey: [PAYMENT_DEFAULTS_KEY, userData?.id],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_my_payment_defaults' as any);
            if (error) throw error;
            return data as IPaymentDefaults;
        },
        enabled: !!userData?.id,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const saveMutation = useMutation({
        mutationFn: async (form: Omit<IPaymentOptionsForm, 'methods'>) => {
            const { data, error } = await supabase.rpc('save_my_payment_defaults' as any, {
                p_defaults: { ...form, bank_iban: form.bank_iban.replace(/\s+/g, '').toUpperCase() },
            });
            if (error) throw error;
            return data as IPaymentDefaults;
        },
        onSuccess: (data) => {
            queryClient.setQueryData([PAYMENT_DEFAULTS_KEY, userData?.id], data);
            // Le proposte non ancora configurate si precompilano da qui.
            queryClient.invalidateQueries({ queryKey: [PAYMENT_OPTIONS_KEY] });
        },
    });

    return {
        defaults: query.data,
        isLoading: query.isLoading,
        save: saveMutation.mutateAsync,
        isSaving: saveMutation.isPending,
    };
};

/** Errore RPC → chiave i18n. */
export const paymentOptionsErrorKey = (error: unknown): TranslationKey => {
    const message = (error as { message?: string } | null)?.message ?? '';
    for (const code of ['bank_details_required', 'invalid_iban', 'payment_method_required', 'forbidden']) {
        if (message.includes(code)) return `paymentOptions.errors.${code}` as TranslationKey;
    }
    return 'paymentOptions.errors.generic';
};
